export const getRuleFormulas = (jsonForm = {}, rule = {}) => {
  return rule.formulas || jsonForm.formulas || []
}

export const isEmpty = (value) => {
  if (value === '' || value === undefined || value === null) return true
  if (Array.isArray(value) && value.length === 0) return true
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).length === 0
  ) {
    return true
  }
  return false
}

export const isNumber = (value) => {
  if (value === '' || value === null || value === undefined) return false
  return !Number.isNaN(Number(value))
}

export const getRuleFormula = (jsonForm = {}, action = {}, rule = {}) => {
  let config = action.config && typeof action.config === 'object' ? action.config : {}
  let formulas = getRuleFormulas(jsonForm, rule)
  let formulaId = config.formulaId || action.formulaId
  let formula = formulas.find(({ id }) => id === formulaId)

  return formula || {
    id: formulaId,
    name: config.formulaName,
    formula: config.formula || action.value || '',
    precision: config.precision,
    emptyValue: config.emptyValue,
  }
}

const getFormulaTokens = (formula = {}) => {
  if (Array.isArray(formula.tokens)) return formula.tokens

  return (
    String(formula.formula || '').match(
      /{{[^}]+}}|@[A-Za-z0-9_.:-]+|\d+(\.\d+)?|\*\*|[()+\-*/^]/g,
    ) || []
  )
    .filter((value) => value)
    .map((value) => ({
      type: ['+', '-', '*', '/', '**', '^', '(', ')'].includes(value)
        ? 'symbol'
        : /^@formula:/.test(value) || /^{{formula:.*}}$/.test(value)
        ? 'formula'
        : /^@/.test(value)
        ? 'field'
        : 'raw',
      value,
      label: value,
    }))
}

const getTokenValue = (token = '') => {
  return String(token || '')
    .replace(/^{{|}}$/g, '')
    .replace(/^@/, '')
}

const normalizeFormulaSymbol = (symbol = '') => {
  switch (symbol) {
    case '×':
    case 'ร—':
      return '*'
    case '÷':
    case 'รท':
      return '/'
    case '−':
    case 'โ’':
      return '-'
    default:
      return symbol
  }
}

const getFormulaTokenFieldValues = (token = {}) => {
  let data = token.valueOption?.data || {}
  let values = [
    token.value,
    token.displayValue,
    token.label,
    token.valueOption?.value,
    data.actionOptionValue,
    data.optionValue,
    data.valueKey,
    data.field,
    data.key,
    data.name,
    data.fieldName,
    data.fieldPath,
  ]

  let fieldValues = values
    .map((value) => getTokenValue(value))
    .filter((value) => value)
  let fallbackValues = fieldValues
    .map((value) => String(value || '').trim())
    .reduce((list, value) => {
      return [
        ...list,
        value,
        value.replace(/\s+/g, ''),
        value.toLowerCase(),
      ]
    }, [])

  return fallbackValues.filter(
    (value, index, list) => value && list.indexOf(value) === index,
  )
}

const getFormulaTokenRuleValue = (
  designContext = {},
  token = {},
  source = 'formData',
  data = {},
  getRuleValue = () => undefined,
) => {
  let value
  let fieldValues = getFormulaTokenFieldValues(token)

  fieldValues.find((field) => {
    value = getRuleValue(designContext, { source, field }, data)
    return !isEmpty(value)
  })

  return value
}

const createFormulaNumber = (value, emptyValue = 0) => {
  if (isEmpty(value)) return Number(emptyValue || 0)
  if (isNumber(value)) return Number(value)
  return Number(emptyValue || 0)
}

const isOperator = (token) => ['+', '-', '*', '/', '**', '^'].includes(token)

const getOperatorPriority = (operator) => {
  if (['**', '^'].includes(operator)) return 3
  if (['*', '/'].includes(operator)) return 2
  if (['+', '-'].includes(operator)) return 1
  return 0
}

const calculateTwoValue = (left, right, operator) => {
  switch (operator) {
    case '+':
      return left + right
    case '-':
      return left - right
    case '*':
      return left * right
    case '/':
      return left / right
    case '**':
    case '^':
      return left ** right
    default:
      return right
  }
}

const createExpressionTokens = (expression = '') => {
  let tokens = expression.match(/\d+(\.\d+)?|\*\*|[()+\-*/^]/g) || []
  let result = []

  tokens.forEach((token, index) => {
    let prevToken = result[result.length - 1]
    let nextToken = tokens[index + 1]
    let isNegativeNumber =
      token === '-' &&
      isNumber(nextToken) &&
      (!prevToken || isOperator(prevToken) || prevToken === '(')

    if (isNegativeNumber) {
      result.push(String(Number(nextToken) * -1))
      tokens[index + 1] = ''
    } else if (token) {
      result.push(token)
    }
  })

  return result
}

const calculateExpression = (expression = '') => {
  let values = []
  let operators = []

  const calculateLast = () => {
    let operator = operators.pop()
    let right = Number(values.pop())
    let left = Number(values.pop())

    values.push(calculateTwoValue(left, right, operator))
  }

  createExpressionTokens(expression).forEach((token) => {
    if (isNumber(token)) {
      values.push(Number(token))
      return
    }

    if (token === '(') {
      operators.push(token)
      return
    }

    if (token === ')') {
      while (operators.length && operators[operators.length - 1] !== '(') {
        calculateLast()
      }
      operators.pop()
      return
    }

    if (isOperator(token)) {
      while (
        operators.length &&
        isOperator(operators[operators.length - 1]) &&
        getOperatorPriority(operators[operators.length - 1]) >=
          getOperatorPriority(token)
      ) {
        calculateLast()
      }
      operators.push(token)
    }
  })

  while (operators.length) {
    calculateLast()
  }

  return values[0]
}

export const calculateRuleFormula = (
  designContext = {},
  formula = {},
  data = {},
  rule = {},
  formulaIdStack = [],
  options = {},
) => {
  let { jsonForm = {} } = designContext
  let { getRuleValue = () => undefined } = options
  let emptyValue = formula.emptyValue ?? 0
  let expression = getFormulaTokens(formula)
    .map((token) => {
      let tokenValue = normalizeFormulaSymbol(token.value || '')
      if (token.type === 'symbol') return tokenValue
      if (['+', '-', '*', '/', '**', '^', '(', ')'].includes(tokenValue)) {
        return tokenValue
      }
      if (token.type === 'custom') return createFormulaNumber(tokenValue, emptyValue)
      if (token.type === 'field' || token.type === 'datasource') {
        let source = token.type === 'datasource' ? 'datasource' : 'formData'
        let value = getFormulaTokenRuleValue(
          designContext,
          token,
          source,
          data,
          getRuleValue,
        )
        return createFormulaNumber(value, emptyValue)
      }
      if (
        token.type === 'formula' ||
        /^{{formula:.*}}$/.test(tokenValue) ||
        /^@formula:/.test(tokenValue)
      ) {
        let formulaId = getTokenValue(tokenValue).replace(/^formula:/, '')
        if (formulaIdStack.includes(formulaId)) return createFormulaNumber(0)

        let childFormula = getRuleFormulas(jsonForm, rule).find(
          ({ id }) => id === formulaId,
        )
        let value = childFormula
          ? calculateRuleFormula(
              designContext,
              childFormula,
              data,
              rule,
              [...formulaIdStack, formulaId],
              options,
            )
          : 0
        return createFormulaNumber(value, emptyValue)
      }
      if (/^{{.*}}$/.test(tokenValue) || /^@/.test(tokenValue)) {
        let value = getFormulaTokenRuleValue(
          designContext,
          token,
          'formData',
          data,
          getRuleValue,
        )
        return createFormulaNumber(value, emptyValue)
      }

      return tokenValue
    })
    .join(' ')

  if (!/^[0-9+\-*/^().\s]+$/.test(expression)) return undefined

  let result = calculateExpression(expression)
  if (!Number.isFinite(result)) return undefined

  if (formula.precision !== undefined && formula.precision !== '') {
    return Number(result.toFixed(Number(formula.precision)))
  }
  return result
}

export const onRuleCalculate = (
  designContext = {},
  action = {},
  data = {},
  rule = {},
  options = {},
) => {
  let {
    getRuleValue = () => undefined,
    setRuleFormData = () => undefined,
    target = '',
  } = options
  let { jsonForm = {} } = designContext
  let formula = getRuleFormula(jsonForm, action, rule)
  let value = calculateRuleFormula(designContext, formula, data, rule, [], {
    getRuleValue,
  })

  if (value !== undefined) {
    setRuleFormData(designContext, target, value)
  }

  return value
}
